import type { MentorshipProgramDetail } from '@/shared/job-mentorship';
import type { GuidelineBlock, MentorEntry, ProgramHero } from '@/features/mentorship/types/mentorship.types';
import type { JobMentorshipProgramListRow } from '@/features/mentorship/types/mentorship-api.types';
import {
  mentorshipMentorImageUrl,
  mentorshipProgramCoverUrl,
  mentorshipProgramSocialUrl,
} from '@/features/mentorship/lib/media-url';

function programSocialImageValFromDetail(
  detail: MentorshipProgramDetail,
): string | null {
  const row = detail.medias?.find(
    (m) =>
      m.mediaType === 'image' &&
      m.ordering === 0 &&
      m.active &&
      m.file?.trim(),
  );
  return row?.file?.trim() ?? null;
}

function formatMentorshipDateRange(startOn: number, endOn: number): string {
  const fmt = (ts: number) =>
    ts > 0
      ? new Date(ts * 1000).toLocaleDateString('mn-MN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : '';
  const a = fmt(startOn);
  const b = fmt(endOn);
  if (a && b) return `${a} — ${b}`;
  return a || b || '';
}

function buildGuidelinesFromDetail(detail: MentorshipProgramDetail): GuidelineBlock[] {
  const blocks: GuidelineBlock[] = [];
  const para = (title: string, body: string | null | undefined) => {
    const t = body?.trim();
    if (t) {
      blocks.push({ type: 'paragraph', title, body: t });
    }
  };
  para('Тайлбар', detail.program.descr);
  para('Зорилго', detail.program.mission);
  para('Хамрах хүрээ', detail.program.within);
  para('Онцлогууд', detail.program.features);
  para('Шаардлага', detail.program.requirements);
  return blocks;
}

/** Жагсаалт, hero, card-д ашиглана. */
export type MentorshipListProgramView = {
  id: number;
  listTitle: string;
  cardImage: string;
  cardImageAlt: string;
  dateLabel: string;
  shortDescription: string;
  heroBackgroundUrl: string;
  heroTitleAlt: string;
};

export function mapListRowToView(row: JobMentorshipProgramListRow): MentorshipListProgramView {
  const bannerUrl = mentorshipProgramCoverUrl(row.poster);
  const socialKey = row.socialImage?.trim();
  const cardImageUrl = socialKey
    ? mentorshipProgramSocialUrl(socialKey)
    : bannerUrl;
  const descr = row.descr.trim();
  return {
    id: row.id,
    listTitle: row.title,
    cardImage: cardImageUrl,
    cardImageAlt: row.title,
    dateLabel: formatMentorshipDateRange(row.startOn, row.endOn),
    /** HTML (жишээ нь TipTap); карт дээр `line-clamp`-аар хязгаарлана — тэгшлэн таслахгүй. */
    shortDescription: descr,
    heroBackgroundUrl: bannerUrl,
    heroTitleAlt: row.title,
  };
}

export type MentorshipDetailView = {
  programId: number;
  jobId: number;
  canApply: boolean;
  applyClosedReason: MentorshipProgramDetail['applyClosedReason'];
  listTitle: string;
  metaTitle: string;
  metaDescription: string;
  ogImageUrl: string;
  hero: ProgramHero;
  guidelineHeading: string;
  guidelineSubheading: string;
  guidelines: GuidelineBlock[];
  mentors: MentorEntry[];
  questions: MentorshipProgramDetail['questions'];
};

export function mapDetailApiToView(detail: MentorshipProgramDetail): MentorshipDetailView {
  const p = detail.program;
  const bannerUrl = mentorshipProgramCoverUrl(p.poster);
  const socialKey = programSocialImageValFromDetail(detail);
  const ogImageUrl = socialKey
    ? mentorshipProgramSocialUrl(socialKey)
    : bannerUrl;
  const canApply = detail.canApply !== false;
  const dateLabel = formatMentorshipDateRange(p.startOn, p.endOn);

  const mentors: MentorEntry[] = [...detail.mentors]
    .filter((m) => m.active)
    .sort((a, b) => a.ordering - b.ordering)
    .map((m) => {
      const cdn = (process.env.NEXT_PUBLIC_CDN_URL || '').replace(/\/$/, '');
      const fallback =
        cdn && m.id
          ? `${cdn}/mentorship/mentor_${m.id}.png`
          : '/mentor/mentor-star.png';
      return {
        id: m.id,
        name: m.fullname,
        company: m.companyName,
        role: m.position,
        imageSrc: m.image ? mentorshipMentorImageUrl(m.image) : fallback,
      };
    });

  const hero: ProgramHero = {
    backgroundImage: bannerUrl,
    logoAlt: p.title,
    titleAlt: p.title,
    showDownArrow: true,
    dateStart: dateLabel.split('—')[0]?.trim() || '',
    dateEnd: dateLabel.split('—')[1]?.trim() || '',
  };

  return {
    programId: p.id,
    jobId: p.jobId,
    canApply,
    applyClosedReason: detail.applyClosedReason,
    listTitle: p.title,
    metaTitle: `${p.title} | Internship & Mentorship | Zangia.mn`,
    metaDescription: p.descr.trim() || p.title,
    ogImageUrl,
    hero,
    guidelineHeading: p.title,
    guidelineSubheading: dateLabel,
    guidelines: buildGuidelinesFromDetail(detail),
    mentors,
    questions: detail.questions,
  };
}
